import { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, CheckCircle2, Loader2 } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/ui-kit/Reveal";
import { CONTACT, SERVICES_NAV } from "@/lib/site";

export const CONTACT_INTRO = "Tell us about your premises and the support you need. Our team will get back to you with a tailored proposal.";

const API = process.env.REACT_APP_BACKEND_URL;
const CHANNELS = [
  { id: "phone", icon: Phone, label: "Call us", numbers: [CONTACT.phone, ...CONTACT.mobiles] },
  { id: "whatsapp", icon: MessageCircle, label: "WhatsApp", value: "Chat with us", href: `https://wa.me/${CONTACT.whatsapp.replace(/[^0-9]/g, "")}` },
  { id: "email", icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { id: "address", icon: MapPin, label: "Office", value: CONTACT.address },
  { id: "hours", icon: Clock, label: "Working hours", value: CONTACT.hours },
];
const EMPTY = { name: "", phone: "", email: "", company: "", service: SERVICES_NAV[0].label, message: "", website: "" };
const field = "w-full bg-[var(--paper)] border-b-2 border-slate-200 focus:border-[var(--red)] outline-none px-4 py-3.5 text-[15px] text-[var(--ink)] transition-colors";

const Channel = ({ c }) => {
  const Tag = c.href ? "a" : "div";
  return (
    <Tag data-testid={`contact-channel-${c.id}`} {...(c.href ? { href: c.href, target: c.id === "whatsapp" ? "_blank" : undefined, rel: "noreferrer" } : {})}
      className="group flex gap-4 py-5 border-b border-white/10">
      <span className="w-11 h-11 shrink-0 grid place-items-center bg-[var(--red)] chamfer-sm transition-transform duration-500 group-hover:-rotate-6"><c.icon className="w-5 h-5" /></span>
      <span>
        <span className="block text-xs font-bold uppercase tracking-[0.2em] text-white/70">{c.label}</span>
        {c.numbers ? (
          <span className="block mt-1 text-base sm:text-lg font-semibold">
            {c.numbers.map((n, i) => (
              <span key={n}>
                <a data-testid={`contact-number-${i}`} href={`tel:${n.replace(/[^0-9+]/g, "")}`} className="whitespace-nowrap hover:text-[#ff6b70] transition-colors">{n}</a>
                {i < c.numbers.length - 1 && ", "}
              </span>
            ))}
          </span>
        ) : (
          <span className={`block mt-1 text-base sm:text-lg font-semibold [overflow-wrap:anywhere] ${c.href ? "group-hover:text-[#ff6b70] transition-colors" : ""}`}>{c.value}</span>
        )}
      </span>
    </Tag>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">{label}</span>
    {children}
  </label>
);

export default function ContactContent() {
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState("sending"); setError("");
    try {
      const res = await fetch(`${API}/api/enquiries`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data?.detail === "string" ? data.detail : "Please check your details and try again.");
      }
      setState("sent"); setForm(EMPTY);
    } catch (err) {
      setState("idle"); setError(err?.message || "Something went wrong. Please call us instead.");
    }
  };

  return (
    <section data-testid="contact-section" className="bg-white py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <Reveal className="lg:col-span-5">
          <div className="chamfer grain relative bg-[var(--navy)] text-white p-8 sm:p-10 h-full">
            <Eyebrow light>Reach us</Eyebrow>
            <h2 className="mt-5 font-display italic font-black uppercase text-4xl sm:text-5xl leading-[0.9]">Talk to <span className="text-[#ff4d53]">our team</span></h2>
            <div className="mt-6">{CHANNELS.map((c) => <Channel key={c.id} c={c} />)}</div>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          {state === "sent" ? (
            <div data-testid="contact-form-success" className="h-full flex flex-col justify-center items-start bg-[var(--paper)] chamfer-sm p-10">
              <CheckCircle2 className="w-14 h-14 text-[var(--navy)]" />
              <h3 className="mt-6 font-display italic font-black uppercase text-4xl text-[var(--ink)]">Thank you!</h3>
              <p className="mt-3 text-slate-600 max-w-md">Your enquiry has reached our team. We will contact you shortly.</p>
              <button type="button" data-testid="contact-form-send-another" onClick={() => setState("idle")} className="mt-8 text-sm font-bold uppercase tracking-wider text-[var(--red)]">Send another enquiry</button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="grid sm:grid-cols-2 gap-6">
              <div className="sm:col-span-2">
                <Eyebrow>Get a quote</Eyebrow>
                <h2 className="mt-5 font-display italic font-black uppercase text-4xl sm:text-5xl leading-[0.9] text-[var(--ink)]">Send us an <span className="text-[var(--red)]">enquiry</span></h2>
              </div>
              <Field label="Full name *"><input data-testid="contact-input-name" required minLength={2} value={form.name} onChange={set("name")} className={field} /></Field>
              <Field label="Phone *"><input data-testid="contact-input-phone" required type="tel" minLength={7} value={form.phone} onChange={set("phone")} className={field} /></Field>
              <Field label="Email *"><input data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")} className={field} /></Field>
              <Field label="Company / Site"><input data-testid="contact-input-company" value={form.company} onChange={set("company")} className={field} /></Field>
              <div className="sm:col-span-2">
                <Field label="Service required *">
                  <select data-testid="contact-select-service" value={form.service} onChange={set("service")} className={field}>
                    {[...SERVICES_NAV.map((s) => s.label), "Other"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Message *"><textarea data-testid="contact-input-message" required minLength={5} rows={5} value={form.message} onChange={set("message")} className={`${field} resize-none`} /></Field>
              </div>
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
              {error && <p data-testid="contact-form-error" className="sm:col-span-2 text-sm font-semibold text-[var(--red)]">{error}</p>}
              <div className="sm:col-span-2">
                <button type="submit" data-testid="contact-form-submit" disabled={state === "sending"}
                  className="group btn-cut inline-flex items-center gap-2 font-bold uppercase tracking-wider text-sm px-9 py-4 bg-[var(--red)] text-white hover:bg-[var(--red-hover)] disabled:opacity-70 transition-[background-color,transform] duration-300 active:scale-[0.97]">
                  {state === "sending" ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send enquiry <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
